import React, { useState, useEffect } from 'react';
import { X, ArrowDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';

interface ConfirmBookingMoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updatedDates: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  }) => void;
  bookingTitle: string;
  oldStartDate: Date;
  oldEndDate: Date;
  newStartDate: Date;
  newEndDate: Date;
  isSaving?: boolean;
}

export default function ConfirmBookingMoveModal({
  isOpen,
  onClose,
  onConfirm,
  bookingTitle,
  oldStartDate,
  oldEndDate,
  newStartDate,
  newEndDate,
  isSaving = false
}: ConfirmBookingMoveModalProps) {
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedEndDate, setEditedEndDate] = useState('');
  const [editedEndTime, setEditedEndTime] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  // Update state when props change
  useEffect(() => {
    setEditedStartDate(format(newStartDate, 'yyyy-MM-dd'));
    setEditedStartTime(format(newStartDate, 'HH:mm'));
    setEditedEndDate(format(newEndDate, 'yyyy-MM-dd'));
    setEditedEndTime(format(newEndDate, 'HH:mm'));
  }, [newStartDate, newEndDate]);

  // Handle animation when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  const handleClose = () => {
    if (isSaving) return;
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    onConfirm({
      startDate: editedStartDate,
      startTime: editedStartTime,
      endDate: editedEndDate,
      endTime: editedEndTime
    });
  };

  // Format dates for display
  const formatDateTime = (date: Date) => {
    return format(date, 'MMM d, yyyy h:mm a');
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[999999] flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen ? 'bg-black/40 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
      style={{
        pointerEvents: isAnimating ? 'auto' : 'none'
      }}
    >
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden transition-all duration-300 ease-out ${
          isOpen 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={e => e.stopPropagation()}
        style={{
          transitionTimingFunction: isOpen ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'cubic-bezier(0.4, 0, 1, 1)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {bookingTitle}
          </h2>
          {!isSaving && (
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Confirmation Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-sm text-blue-900 font-medium text-center">
              📅 Confirm the new schedule for this booking
            </p>
          </div>

          {/* Current Schedule - Read Only */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Current Schedule
            </label>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-1">
              <p className="text-sm text-red-700">
                <span className="font-medium">Start:</span> {formatDateTime(oldStartDate)}
              </p>
              <p className="text-sm text-red-700">
                <span className="font-medium">End:</span> {formatDateTime(oldEndDate)}
              </p>
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center">
            <div className="p-2 bg-gray-100 rounded-full">
              <ArrowDown className="h-4 w-4 text-gray-600" />
            </div>
          </div>

          {/* New Schedule - Editable */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              New Start Date & Time
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={editedStartDate}
                onChange={(e) => setEditedStartDate(e.target.value)}
                disabled={isSaving}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
              />
              <input
                type="time"
                value={editedStartTime}
                onChange={(e) => setEditedStartTime(e.target.value)}
                disabled={isSaving}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              New End Date & Time
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={editedEndDate}
                onChange={(e) => setEditedEndDate(e.target.value)}
                disabled={isSaving}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
              />
              <input
                type="time"
                value={editedEndTime}
                onChange={(e) => setEditedEndTime(e.target.value)}
                disabled={isSaving}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Footer - iOS style */}
        <div className="px-5 py-4 bg-gray-50/50 border-t border-gray-100 space-y-2">
          <button
            onClick={handleConfirm}
            disabled={isSaving}
            className="w-full px-4 py-3 text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            {isSaving ? 'Moving...' : 'Confirm Move'}
          </button>
          {!isSaving && (
            <button
              onClick={handleClose}
              className="w-full px-4 py-2.5 text-gray-600 hover:text-gray-800 transition-colors font-medium text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
